<script>
import TwModal from '@/components/generic/modal/tw-modal.vue'
import TwInput from '@/components/generic/forms/tw-input.vue'
import TwValidationErrors from '@/components/generic/tw-validation-errors.vue'
import { useFundraiserStore } from '@/stores/fundraiser.js'
import TwSelect from '@/components/generic/forms/tw-select.vue'
import { useOrganizationStore } from '@/stores/organization.js'
import modalCascadeMixin from '@/mixins/modalCascadeMixin.js'
import { usePaymentServiceStore } from '@/stores/paymentServices.js'
import TwCheckbox from '@/components/generic/forms/tw-checkbox.vue'
import TwAlert from '@/components/generic/tw-alert.vue'
import TwCard from '@/components/generic/tw-card.vue'
import TwAmount from '@/components/generic/forms/tw-amount.vue'
import TwDate from '@/components/generic/forms/tw-date.vue'
import TwRadio from '@/components/generic/forms/tw-radio.vue'
import TwWysiwyg from '@/components/generic/forms/tw-wysiwyg.vue'

export default {
  name: 'FundraiserCreateModal',
  components: { TwWysiwyg, TwRadio, TwDate, TwAmount, TwCard, TwAlert, TwCheckbox, TwSelect, TwValidationErrors, TwInput, TwModal },
  mixins:[modalCascadeMixin],
  props: {
    beneficiary: {
      type: Object,
      default: null
    },
    paymentService: {
      type: Object,
      default: null
    }
  },
  emits: ['created'],
  data() {
    return {
      Loading: false,
      ShowDetails: false,
      ValidationErrorMessage: null,
      FundraiserBeneficiary: null,
      FundraiserPaymentServiceId: null,
      FundraiserTitle: null,
      FundraiserGoal: null,
      FundraiserGoalHidden: false,
      FundraiserPagesEnabled: false,
      FundraiserDeadline: null,
      FundraiserNoDeadline: false,
      FundraiserShortDescription: null,
      FundraiserPagesDeadline: null,
      FundraiserPagesDeadlineLocked: false,
      FundraiserPageGoal: null,
      FundraiserPageGoalLocked: false
    }
  },
  computed: {
    organizations() {
      return useOrganizationStore().getItems
    },
    paymentServices() {
      if(this.FundraiserBeneficiary && this.FundraiserBeneficiary.object_type === 'App\\Models\\Organization') {
        return usePaymentServiceStore().getItemsByOrganizationId(this.FundraiserBeneficiary?.id).filter( item => item.usable_in_frontend)
      }
      return []
    },
    formValid() {
      return this.FundraiserBeneficiary && this.FundraiserPaymentServiceId && this.FundraiserTitle && this.FundraiserTitle.length > 2
    }
  },
  watch: {
    async FundraiserBeneficiary() {
      // this.FundraiserPaymentServiceId = null
      const defaultPaymentService = this.paymentServices.find(paymentService => paymentService.default)
      if(defaultPaymentService) {
        this.FundraiserPaymentServiceId = defaultPaymentService.id
      }
    },
    FundraiserGoal(newVal, oldVal) {
      if(!this.FundraiserPageGoal || this.FundraiserPageGoal === oldVal) {
        this.FundraiserPageGoal = newVal
      }
    },
    FundraiserDeadline(newVal, oldVal) {
      if(!this.FundraiserPagesDeadline || this.FundraiserPagesDeadline === oldVal) {
        this.FundraiserPagesDeadline = newVal
      }
    },
    FundraiserNoDeadline(newVal) {
      if(newVal === true) {
        this.FundraiserDeadline = null
        this.FundraiserPagesDeadline = null
      }
    }
  },
  mounted() {
    if(this.beneficiary) {
      this.FundraiserBeneficiary = this.beneficiary
    }
    if(this.paymentService) {
      this.FundraiserPaymentServiceId = this.paymentService.id
    }
  },
  methods: {
    async open() {
      this.$refs.modal.open()
      this.Loading = true
      // we fetch all the organizations
      await useOrganizationStore().fetchItems({
        clear: true,
        not_paginated: true
      })
      this.FundraiserBeneficiary = this.organizations[0]
      // we fetch the payment services for this organization
      await usePaymentServiceStore().fetchItems({
        payment_service_for_id: this.FundraiserBeneficiary.id,
        payment_service_for_type: this.FundraiserBeneficiary.object_type,
        not_paginated: true,
      })
      if(!this.FundraiserPaymentServiceId) {
        this.FundraiserPaymentServiceId = this.paymentServices[0]?.id
      }
      if(this.organizations.length > 1 || this.paymentServices.length > 1) {
        this.ShowDetails = true
      }
      this.Loading = false
    },
    async fundraiserCreate() {
      try {
        this.Loading = true
        this.ValidationErrorMessage = null
        const fundraiser = await useFundraiserStore().itemCreate({
          title: this.FundraiserTitle,
          beneficiary_type: this.FundraiserBeneficiary.object_type,
          beneficiary_id: this.FundraiserBeneficiary.id,
          payment_service_id: this.FundraiserPaymentServiceId,
          goal: this.FundraiserGoal,
          goal_hidden: this.FundraiserGoalHidden,
          deadline: this.FundraiserDeadline,
          no_deadline: this.FundraiserNoDeadline,
          short_description: this.FundraiserShortDescription,
          enable_supporters: this.FundraiserPagesEnabled,
          default_deadline: this.FundraiserPagesDeadline,
          lock_deadline: this.FundraiserPagesDeadlineLocked,
          default_goal: this.FundraiserPageGoal,
          lock_goal: this.FundraiserPageGoalLocked
        })
        this.Loading = false
        if(fundraiser) {
          this.$refs.modal.close()
          this.$emit('created', fundraiser)
          this.FundraiserTitle = null
        }
        else {
          this.ValidationErrorMessage = { message: 'Failed to create fundraiser'}
        }
      } catch (e) {
        console.error(e)
        this.Loading = false
        if(e.response) {
          this.ValidationErrorMessage = e.response.data
        }
        throw e
      }
    }
  }
}
</script>

<template>
  <tw-modal ref="modal" :loading="Loading" modal-id="fundraiser-create-modal" :ok-disabled="!formValid" :ok-text="'Create ' + $t('fundraiser')" @ok="fundraiserCreate">
    <h2>
      Create a fundraising {{ $t('fundraiser') }}
    </h2>
    <p class="mt-6 mb-8">
      Setup your GroupRev {{ $t('fundraiser') }} with a few short details to get started.
    </p>
    <div class="flex form-spacing">
      <tw-input v-model="FundraiserTitle">
        <template #label>
          Give this {{ $t('fundraiser') }} a title
        </template>
      </tw-input>

      <div>
        <tw-radio v-model="FundraiserPagesEnabled" label="Group fundraising" :options="[{text:'Enabled', value: true},{text: 'Disabled', value: false}]" />
      </div>
      <div class="grid md:grid-cols-2 gap-6">
        <div>
          <tw-amount v-model="FundraiserGoal" prepend="$" label="Enter your goal amount" />
          <tw-checkbox v-model="FundraiserGoalHidden" class="mt-3">
            <span>Hide {{ $t('fundraiser') }} goal from landing page</span>
          </tw-checkbox>
        </div>
        <div v-if="FundraiserPagesEnabled">
          <tw-amount v-model="FundraiserPageGoal" label="Group Member Fundraising Goal" />
          <tw-checkbox v-model="FundraiserPageGoalLocked" class="mt-3">
            <span>Group members cannot change the goal</span>
          </tw-checkbox>
        </div>
      </div>
      <div class="grid md:grid-cols-2 gap-6">
        <div>
          <tw-date v-model="FundraiserDeadline" :disabled="FundraiserNoDeadline" label="Fundraising Deadline" />
          <tw-checkbox v-model="FundraiserNoDeadline" class="mt-3">
            <span class="capitalize">This {{ $t('fundraiser') }}  is On-Going</span>
          </tw-checkbox>
        </div>
        <div v-if="FundraiserPagesEnabled">
          <tw-date v-model="FundraiserPagesDeadline" label="Group Member Fundraising Deadline" />
          <tw-checkbox v-model="FundraiserPagesDeadlineLocked">
            <span class="capitalize">Group members cannot change the deadline</span>
          </tw-checkbox>
        </div>
      </div>
      <tw-input v-model="FundraiserShortDescription">
        <template #label>
          <span> Short description: </span>
        </template>
      </tw-input>

      <!--    <tw-checkbox v-model="ShowDetails">-->
      <!--      Show Advanced Settings-->
      <!--    </tw-checkbox>-->
      <tw-card v-if="!paymentServices || paymentServices.length === 0" class="bg-red-50 text-red-700">
        You do not have any payment gateways set up. Please set up a payment gateway in the organization settings.
      </tw-card>
      <div v-show="ShowDetails" class="form-spacing">
        <tw-select v-if="!beneficiary" v-model="FundraiserBeneficiary" label-key="name" :options="organizations" label="Fundraiser organization:" />
        <tw-select v-if="FundraiserBeneficiary && paymentServices.length > 0 && !paymentService" v-model="FundraiserPaymentServiceId" label-key="provider_friendly_name" value-key="id" :options="paymentServices" label="Payment service to use for the fundraiser" />
      </div>
      <tw-validation-errors :validation-result-error="ValidationErrorMessage" />
    </div>
  </tw-modal>
</template>

<style scoped>

</style>
